/**
 * Lightweight client-side perf + error logger.
 * Stores a ring buffer of recent entries in localStorage so the admin dashboard
 * can surface stalled requests and slow page loads without a 3rd-party service.
 */

export type PerfEntry = {
  id: string;
  ts: number;
  scope: string;          // e.g. "conference-hub"
  name: string;           // e.g. "regions", "rpc:get_public_registration_counts"
  durationMs: number;
  ok: boolean;
  status?: "ok" | "error" | "timeout";
  error?: string;
  meta?: Record<string, unknown>;
};

const KEY = "ofuesi:perf-log";
const MAX = 200;

const safeRead = (): PerfEntry[] => {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const safeWrite = (entries: PerfEntry[]) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(entries.slice(-MAX)));
  } catch {
    /* quota / disabled storage — ignore */
  }
};

export const recordPerf = (entry: Omit<PerfEntry, "id" | "ts">) => {
  const full: PerfEntry = {
    id: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
    ts: Date.now(),
    ...entry,
  };
  const next = [...safeRead(), full];
  safeWrite(next);
  if (!entry.ok || entry.durationMs > 2000) {
    // Surface slow / failed requests in the console for easy triage.
    // eslint-disable-next-line no-console
    console.warn(
      `[perf] ${entry.scope}:${entry.name} ${entry.durationMs}ms ${entry.status || (entry.ok ? "ok" : "error")}`,
      entry.error || "",
    );
  }
  return full;
};

/**
 * Wrap an async operation, recording its duration and outcome.
 * Adds a soft timeout that records a "timeout" entry but still awaits the original promise.
 */
export const trackAsync = async <T>(
  scope: string,
  name: string,
  fn: () => Promise<T>,
  opts: { timeoutMs?: number; meta?: Record<string, unknown> } = {},
): Promise<T> => {
  const start = performance.now();
  const { timeoutMs = 5000, meta } = opts;
  let timedOut = false;
  const timer = window.setTimeout(() => {
    timedOut = true;
    recordPerf({
      scope,
      name,
      durationMs: Math.round(performance.now() - start),
      ok: false,
      status: "timeout",
      error: `Exceeded ${timeoutMs}ms`,
      meta,
    });
  }, timeoutMs);

  try {
    const result = await fn();
    if (!timedOut) {
      window.clearTimeout(timer);
      recordPerf({
        scope,
        name,
        durationMs: Math.round(performance.now() - start),
        ok: true,
        status: "ok",
        meta,
      });
    } else {
      recordPerf({
        scope,
        name: `${name}:resolved-after-timeout`,
        durationMs: Math.round(performance.now() - start),
        ok: true,
        status: "ok",
        meta,
      });
    }
    return result;
  } catch (err: any) {
    window.clearTimeout(timer);
    recordPerf({
      scope,
      name,
      durationMs: Math.round(performance.now() - start),
      ok: false,
      status: "error",
      error: err?.message || String(err),
      meta,
    });
    throw err;
  }
};

export const readPerfLog = (): PerfEntry[] => safeRead().slice().reverse();
export const clearPerfLog = () => safeWrite([]);
